import { z } from 'zod';

export const programKindValues = [
  'briefing_induction',
  'briefing_primary_workplace',
  'briefing_repeated',
  'briefing_unscheduled',
  'briefing_targeted',
  'training',
  'knowledge_check',
  'internship_program',
] as const;

export type ProgramKind = (typeof programKindValues)[number];

export const programKindLabels: Record<ProgramKind, string> = {
  briefing_induction: 'Вводный инструктаж',
  briefing_primary_workplace: 'Первичный на раб. месте',
  briefing_repeated: 'Повторный инструктаж',
  briefing_unscheduled: 'Внеплановый инструктаж',
  briefing_targeted: 'Целевой инструктаж',
  training: 'Обучение',
  knowledge_check: 'Проверка знаний',
  internship_program: 'Программа стажировки',
};

export const recurrenceModelValues = ['one_time', 'cyclic'] as const;
export type RecurrenceModel = (typeof recurrenceModelValues)[number];

export const recurrenceModelLabels: Record<RecurrenceModel, string> = {
  one_time: 'Разовое',
  cyclic: 'Периодическое',
};

export const libraryTreeItemSchema = z.object({
  id: z.string(),
  node_type: z.enum(['direction', 'program']),
  name: z.string(),
  parent_id: z.string().nullable(),
  has_children: z.boolean(),
  program_kind: z.string().nullable().optional(),
  recurrence_facet: z.string().nullable().optional(),
  is_level_based_facet: z.boolean().nullable().optional(),
  program_id: z.string().nullable().optional(),
});

export type LibraryTreeItem = z.infer<typeof libraryTreeItemSchema>;

export const libraryTreeResponseSchema = z.object({
  items: z.array(libraryTreeItemSchema),
});

export const programLevelSchema = z.object({
  id: z.string(),
  level_code: z.string(),
  level_name: z.string(),
  level_period_months: z.number().nullable().optional(),
  level_validity_months: z.number().nullable().optional(),
});

export type ProgramLevel = z.infer<typeof programLevelSchema>;

export const passingRulesSchema = z.object({
  recurrence_model: z.string(),
  is_level_based: z.boolean(),
  period_months: z.number().nullable().optional(),
  validity_months: z.number().nullable().optional(),
  min_retry_interval_days: z.number().nullable().optional(),
  max_attempts_per_period: z.number().nullable().optional(),
  unplanned_triggers: z.array(z.string()).nullable().optional(),
});

export type PassingRules = z.infer<typeof passingRulesSchema>;

export const libraryNodeBriefSchema = z.object({
  id: z.string(),
  name: z.string(),
  path: z.string(),
  program_kind: z.string().nullable().optional(),
});

export const programDetailSchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  description: z.string().nullable().optional(),
  library_node: libraryNodeBriefSchema,
  program_kind: z.string().nullable().optional(),
  is_external: z.boolean(),
  passing_rules: passingRulesSchema.nullable().optional(),
  levels: z.array(programLevelSchema),
});

export type ProgramDetail = z.infer<typeof programDetailSchema>;

export const programDetailResponseSchema = z.object({
  program: programDetailSchema,
});

export const trainingSectionSchema = z.object({
  key: z.string(),
});

export type TrainingSection = z.infer<typeof trainingSectionSchema>;

export const trainingSectionsResponseSchema = z.object({
  sections: z.array(trainingSectionSchema),
});

export const sectionKeyValues = [
  'my_training',
  'employees_training_table',
  'problem_employees',
  'training_matrix',
  'training_requests',
  'training_library',
  'internships',
] as const;

export type SectionKey = (typeof sectionKeyValues)[number];

export const sectionKeyLabels: Record<SectionKey, string> = {
  my_training: 'Мои обучения и инструктажи',
  employees_training_table: 'Обучение сотрудников',
  problem_employees: 'Проблемные сотрудники',
  training_matrix: 'Матрица обучения',
  training_requests: 'Заявки на обучение',
  training_library: 'Библиотека обучений',
  internships: 'Стажировка и допуск',
};

export const upcomingActionSchema = z.object({
  type: z.string(),
  due_at: z.string().nullable().optional(),
  program_name: z.string().nullable().optional(),
  program_id: z.string().nullable().optional(),
});

export type UpcomingAction = z.infer<typeof upcomingActionSchema>;

export const employeeTrainingProfileSchema = z.object({
  id: z.string(),
  full_name: z.string(),
  personnel_number: z.string().nullable().optional(),
  department_name: z.string(),
  position: z.string().nullable().optional(),
  workplace: z.string().nullable().optional(),
  work_admission: z.string(),
  upcoming_actions: z.array(upcomingActionSchema),
});

export type EmployeeTrainingProfile = z.infer<typeof employeeTrainingProfileSchema>;

export const employeeTrainingProfileResponseSchema = z.object({
  employee: employeeTrainingProfileSchema,
});

// --- List employees in training (§5.2.1) ---

export const assignmentStatusValues = [
  'assigned',
  'in_progress',
  'passed',
  'overdue',
  'not_passed',
  'not_admitted',
] as const;

export type AssignmentStatus = (typeof assignmentStatusValues)[number];

export const assignmentStatusLabels: Record<AssignmentStatus, string> = {
  assigned: 'Назначено',
  in_progress: 'В процессе',
  passed: 'Пройдено',
  overdue: 'Просрочено',
  not_passed: 'Не пройдено',
  not_admitted: 'Не допущен',
};

export const displayedAssignmentSchema = z.object({
  assignment_id: z.string(),
  program_name: z.string(),
  program_kind: z.string().nullable().optional(),
  recurrence_model: z.string().nullable().optional(),
  is_level_based: z.boolean(),
  status: z.string(),
  due_at: z.string().nullable().optional(),
  valid_until: z.string().nullable().optional(),
});

export type DisplayedAssignment = z.infer<typeof displayedAssignmentSchema>;

export const positionRefSchema = z.object({
  id: z.string(),
  name: z.string(),
});

export const orgUnitRefSchema = z.object({
  id: z.string(),
  name: z.string(),
});

export const trainingEmployeeRowSchema = z.object({
  employee_id: z.string(),
  full_name: z.string(),
  personnel_number: z.string().nullable().optional(),
  position: positionRefSchema.nullable().optional(),
  org_unit: orgUnitRefSchema,
  work_admission: z.string(),
  displayed_assignment: displayedAssignmentSchema.nullable().optional(),
});

export type TrainingEmployeeRow = z.infer<typeof trainingEmployeeRowSchema>;

export const trainingEmployeesResponseSchema = z.object({
  items: z.array(trainingEmployeeRowSchema),
  next_page_token: z.string().nullable().optional(),
});

// --- Employee assignments (§5.2.3) ---

export const assignmentProgramRefSchema = z.object({
  id: z.string(),
  name: z.string(),
  program_kind: z.string().nullable().optional(),
  recurrence_model: z.string().nullable().optional(),
  is_level_based: z.boolean(),
});

export const assignmentLevelRefSchema = z.object({
  id: z.string(),
  level_code: z.string(),
  level_name: z.string(),
});

export const employeeAssignmentSchema = z.object({
  id: z.string(),
  program: assignmentProgramRefSchema,
  level: assignmentLevelRefSchema.nullable().optional(),
  status: z.string(),
  planned_at: z.string().nullable().optional(),
  completed_at: z.string().nullable().optional(),
  valid_until: z.string().nullable().optional(),
});

export type EmployeeAssignment = z.infer<typeof employeeAssignmentSchema>;

export const employeeAssignmentsResponseSchema = z.object({
  assignments: z.array(employeeAssignmentSchema),
});

// --- Assignment detail (§5.3.1) ---

export const assignmentAttemptSchema = z.object({
  attempt_no: z.number(),
  outcome: z.string(),
  passed_at: z.string().nullable().optional(),
  result_score: z.number().nullable().optional(),
});

export type AssignmentAttempt = z.infer<typeof assignmentAttemptSchema>;

export const assignmentDetailSchema = z.object({
  id: z.string(),
  employee_id: z.string(),
  employee_name: z.string(),
  department_name: z.string(),
  program: assignmentProgramRefSchema,
  level: assignmentLevelRefSchema.nullable().optional(),
  status: z.string(),
  planned_at: z.string().nullable().optional(),
  due_at: z.string().nullable().optional(),
  completed_at: z.string().nullable().optional(),
  valid_until: z.string().nullable().optional(),
  assignment_reason: z.string(),
  attempts: z.array(assignmentAttemptSchema),
});

export type AssignmentDetail = z.infer<typeof assignmentDetailSchema>;

export const assignmentDetailResponseSchema = z.object({
  assignment: assignmentDetailSchema,
});

// --- Assignment events (§5.3.2) ---

export const assignmentEventSchema = z.object({
  id: z.string(),
  event_type: z.string(),
  old_value: z.record(z.string(), z.string()).nullable().optional(),
  new_value: z.record(z.string(), z.string()).nullable().optional(),
  changed_by: z.string().nullable().optional(),
  changed_at: z.string(),
  reason: z.string().nullable().optional(),
});

export type AssignmentEvent = z.infer<typeof assignmentEventSchema>;

export const assignmentEventsResponseSchema = z.object({
  events: z.array(assignmentEventSchema),
});

// --- Widgets (§5.1.2) ---

export const widgetAttentionItemSchema = z.object({
  employee_id: z.string(),
  employee_name: z.string(),
  program_name: z.string(),
  kind: z.string(),
  detail: z.string(),
});

export type WidgetAttentionItem = z.infer<typeof widgetAttentionItemSchema>;

export const trainingSummaryWidgetSchema = z.object({
  assigned: z.number(),
  in_progress: z.number(),
  overdue: z.number(),
  expires_in_30_days: z.number(),
  not_passed: z.number(),
  not_admitted: z.number(),
  attention_items: z.array(widgetAttentionItemSchema),
});

export type TrainingSummaryWidget = z.infer<typeof trainingSummaryWidgetSchema>;

export const trainingSummaryWidgetResponseSchema = z.object({
  widget: trainingSummaryWidgetSchema,
});

export const trainingRiskDefaultFiltersSchema = z.object({
  statuses: z.array(z.string()),
  admission_statuses: z.array(z.string()),
});

export const trainingRiskWidgetSchema = z.object({
  total_at_risk: z.number(),
  failed_count: z.number(),
  overdue_count: z.number(),
  not_admitted_count: z.number(),
  delta_to_previous_period: z.number().nullable().optional(),
  severity: z.string(),
  default_filters: trainingRiskDefaultFiltersSchema,
});

export type TrainingRiskWidget = z.infer<typeof trainingRiskWidgetSchema>;

export const trainingRiskWidgetResponseSchema = z.object({
  widget: trainingRiskWidgetSchema,
});

// --- Problem employees (§5.4) ---

export const problemEmployeeRowSchema = z.object({
  employee_id: z.string(),
  full_name: z.string(),
  department_name: z.string(),
  position: z.string().nullable().optional(),
  state: z.string(),
  reason: z.string().nullable().optional(),
  last_decision_at: z.string().nullable().optional(),
});

export type ProblemEmployeeRow = z.infer<typeof problemEmployeeRowSchema>;

export const problemEmployeesSummarySchema = z.object({
  total_at_risk: z.number(),
  not_passed: z.number(),
  not_admitted: z.number(),
  delta_to_previous_period: z.number().nullable().optional(),
});

export type ProblemEmployeesSummary = z.infer<typeof problemEmployeesSummarySchema>;

export const problemEmployeesResponseSchema = z.object({
  items: z.array(problemEmployeeRowSchema),
  summary: problemEmployeesSummarySchema,
  next_page_token: z.string().nullable().optional(),
});

// --- Training matrix ---

export const matrixProgramSchema = z.object({
  program_id: z.string(),
  program_name: z.string(),
  program_code: z.string().nullable().optional(),
  recurrence_model: z.string().nullable().optional(),
  is_external: z.boolean(),
});

export type MatrixProgram = z.infer<typeof matrixProgramSchema>;

export const matrixPositionSchema = z.object({
  position_id: z.string(),
  position_name: z.string(),
  employee_count: z.number(),
  programs: z.array(matrixProgramSchema),
});

export type MatrixPosition = z.infer<typeof matrixPositionSchema>;

export const trainingMatrixResponseSchema = z.object({
  positions: z.array(matrixPositionSchema),
  total_positions: z.number(),
  total_programs: z.number(),
});
